import {
  type ColDef,
  type CellStyle,
  type ValueGetterParams,
  type MenuItemDef,
  type ValueFormatterParams,
  type SetFilterValuesFuncParams,
  type CellStyleFunc,
} from "@ag-grid-community/core"
import {
  getDefaultAddressItems,
  getDefaultTransactionHashItems,
} from "./contextMenuItems"
import { SimpleReadOnlyFloatingFilter } from "./filters/SimpleReadOnlyFloatingFilter"
import { generateIcon } from "./generateIcon"
import { ChainCellRenderer } from "./renderers/ChainCellRenderer"
import { ContractCellRenderer } from "./renderers/ContractCellRenderer"
import { ProjectCellRenderer } from "./renderers/ProjectCellRenderer"

export async function collectionSetFilterValues(
  params: SetFilterValuesFuncParams
) {
  /*
   * This function is critical for the initial load when its column is used for initial grouping at least
   * forEachLeafNode probably throws warnings in some cases because we mainly use SSRM
   */

  if (
    params.colDef.filterParams?.values &&
    // making sure we don't do an infinite loop. If no values param is set on the table itself
    // ag-grid merges these properties which lead to this filterParams.values to be set to this function.
    collectionSetFilterValues.name !== params.colDef.filterParams?.values?.name
  ) {
    await params.colDef.filterParams?.values?.(params)
  } else {
    const availableValues: Array<string | null> = []

    params.api.forEachLeafNode((node) => {
      const value = params.api.getValue(params.column.getId(), node)
      availableValues.push(value)
    })

    params.success(availableValues)
  }
}

function createUnknownValueGetter<T extends Record<string, any>>(
  unknownLabel: string
) {
  return function unknownValueGetter({ data, column }: ValueGetterParams<T>) {
    const value = data?.[column.getColId()]
    if (column.isRowGroupActive()) {
      return value ?? unknownLabel
    } else {
      return value
    }
  }
}

function unknownFilterValueGetter(params: ValueGetterParams<any, string>) {
  const value = params.getValue(params.colDef.field!)
  return value ?? "unknown"
}

function compareStrings(valueA?: string | null, valueB?: string | null) {
  // Unknown equals unknown label ie. what nulls are called
  // If used for sorting columns with different null label => refactor
  return (valueA ?? "Unknown")
    .toLowerCase()
    .localeCompare((valueB ?? "Unknown").toLowerCase(), "en")
}

/** @deprecated */
export const rowIndexColumnType: ColDef<unknown, number> = {
  headerName: "#",
  rowDrag: false,
  pinned: true,
  sortable: false,
  filter: false,
  suppressMenu: true,
  chartDataType: "excluded",
  width: 70,
  cellStyle: (params) => {
    return {
      paddingLeft: `calc(var(--ag-cell-horizontal-padding) * ${Math.max(
        1,
        params.node.uiLevel + 1
      )} - 1px)`,
    }
  },
  valueGetter(params) {
    let currentIndex =
      params.node?.rowIndex != null ? params.node.rowIndex + 1 : null

    if (
      currentIndex !== null &&
      params.node?.parent != null &&
      params.node.parent.rowIndex !== null &&
      params.node.parent.level >= 0
    ) {
      // parent rowIndex starts with 0 so we add 1
      currentIndex -= params.node!.parent.rowIndex + 1
    }

    return currentIndex ?? undefined
  },
}

/** @deprecated */
export const numberColumnType: ColDef<unknown, number> = {
  cellClass: ({ colDef: { cellDataType } }) =>
    `font-mono cell-type-${cellDataType} text-fg-default text-right`,
  headerClass: "text-right",
  filter: "agNumberColumnFilter",
  filterParams: {
    filterOptions: [
      "equals",
      "notEqual",
      "lessThan",
      "lessThanOrEqual",
      "greaterThan",
      "greaterThanOrEqual",
      "inRange",
      "blank",
      "notBlank",
    ],
    blankText: "N/A",
  },
  floatingFilterComponent: SimpleReadOnlyFloatingFilter,
  useValueFormatterForExport: false,
}

/** @deprecated */
export const collectionColumnType: ColDef<unknown, number> = {
  filter: "agMultiColumnFilter",
  filterParams: {
    filters: [
      {
        filter: "agTextColumnFilter",
        display: "subMenu",
        title: "Text filter",
        filterParams: {
          defaultJoinOperator: "OR",
          maxNumConditions: 2, // Swapping to 1 works fine. Using 2 because of subMenu. It might work well enough and provide value
        },
      },
      {
        filter: "agSetColumnFilter",
        filterParams: {
          values: collectionSetFilterValues,
          buttons: ["reset", "apply"],
        },
      },
    ],
  },
}

/** @deprecated */
export const contractColumnType: ColDef<
  Record<string, string | null>,
  string
> = {
  valueGetter: createUnknownValueGetter("Unlabeled"),
  cellRenderer: ContractCellRenderer,
  cellStyle(params): CellStyle {
    let color: string = ""

    if (!params.value || params.value === "Unlabeled") {
      color = "hsl(var(--fg-yellow))"
    }

    return { color }
  },
}

/** @deprecated */
export const trendingColumnType: ColDef<unknown, number> = {
  cellClass: "font-mono",
  cellStyle(params): CellStyle {
    if (!params.value || params.value === 0) {
      return { textAlign: "right", color: "hsl(var(--fg-default))" }
    }

    let color: string = ""
    if (params.value > 0) {
      color = "hsl(var(--fg-emerald))"
    } else if (params.value < 0) {
      color = "hsl(var(--fg-red))"
    } else if (params.value === 0) {
      color = "hsl(var(--fg-default))"
    }

    return { color, textAlign: "right" }
  },
  headerClass: "text-right",
  filter: "agNumberColumnFilter",
  filterParams: {
    filterOptions: [
      "equals",
      "notEqual",
      "lessThan",
      "lessThanOrEqual",
      "greaterThan",
      "greaterThanOrEqual",
      "inRange",
      "blank",
      "notBlank",
    ],
    blankText: "N/A",
  },
  floatingFilterComponent: SimpleReadOnlyFloatingFilter,
  useValueFormatterForExport: false,
}

/** @deprecated */
export const addressColumnType: ColDef<string, number> = {
  // addressType refers to DataTableInner gridOptions.excelStyles
  cellClass: ["font-mono", "addressType"],
  getContextMenuItems(params) {
    const data = params?.node?.data
    const chain = data?.chain_id
    const address = params.value

    return getDefaultAddressItems(params, chain, address)
  },
}

/** @deprecated */
export const transactionHashColumnType: ColDef<string, number> = {
  // transactionHashType refers to DataTableInner gridOptions.excelStyles
  cellClass: ["font-mono", "transactionHashType"],
  getContextMenuItems(params) {
    return getDefaultTransactionHashItems(params)
  },
}

/** @deprecated */
export const urlColumnType: ColDef<string, string> = {
  valueFormatter({ value }) {
    if (typeof value !== "string") {
      return "-"
    }
    return value
      .trim()
      .replace(/http(s?):\/\//, "")
      .replace(/www\./, "")
      .replace(/\/$/, "")
  },
  getContextMenuItems({ defaultItems, value: url }) {
    const currentContextItems: Array<string | MenuItemDef> = defaultItems ?? []

    if (typeof url === "string" && url) {
      currentContextItems.push({
        name: "Open in new tab",
        icon: generateIcon({ icon: "external", width: 8 }),
        action: () => {
          window.open(
            // Make sure we dont link to http
            url.replace("http://", "https://"),
            "_blank"
          )
        },
      })
    }

    return currentContextItems
  },
}

/** @deprecated */
export const trendingInverseColumnType: ColDef<unknown, number> = {
  cellClass: "font-mono",
  cellStyle(params): CellStyle {
    if (!params.value || params.value === 0) {
      return { textAlign: "right", color: "hsl(var(--fg-default))" }
    }

    let color: string = ""
    if (params.value < 0) {
      color = "hsl(var(--fg-emerald))"
    } else if (params.value > 0) {
      color = "hsl(var(--fg-red))"
    } else {
      color = "hsl(var(--fg-default))"
    }

    return { color, textAlign: "right" }
  },
  headerClass: "text-right",
  filter: "agNumberColumnFilter",
  filterParams: {
    filterOptions: [
      "equals",
      "notEqual",
      "lessThan",
      "lessThanOrEqual",
      "greaterThan",
      "greaterThanOrEqual",
      "inRange",
      "blank",
      "notBlank",
    ],
    blankText: "N/A",
  },
  floatingFilterComponent: SimpleReadOnlyFloatingFilter,
  useValueFormatterForExport: false,
}

/** @deprecated */
export const unknownColumnType: ColDef<unknown, string> = {
  filter: "agTextColumnFilter",
  filterValueGetter: unknownFilterValueGetter,
}

/** @deprecated */
export const projectColumnType: ColDef<
  Record<string, string | null>,
  string
> & {
  isSortCaseInsensitive: boolean
} = {
  cellRenderer: ProjectCellRenderer,
  filter: "agTextColumnFilter", // NOTE! In practice this has no effect because collectionColumnType overrides this.
  filterParams: {
    cellRenderer: ProjectCellRenderer,
    cellRendererParams: { isFilterRenderer: true },
  },
  valueGetter: createUnknownValueGetter("Unknown"),
  isSortCaseInsensitive: true,
  // AG Grid always sort set filter list in FE and it is evaluated by comparator
  comparator: compareStrings,
  getContextMenuItems(params, colDef) {
    const currentContextItems: Array<string | MenuItemDef> =
      params.defaultItems ?? []

    if (colDef?.refData) {
      const refData = colDef.refData as Record<string, unknown>
      const projectData = refData[params.value] as
        | Record<string, any>
        | undefined

      if (projectData?.is_public && projectData?.data_id) {
        currentContextItems.push({
          name: "View project",
          action: () => {
            window.open(`/explorer/projects/${projectData?.slug}`, "_blank")
          },
        })
      }
    }

    return currentContextItems
  },
  useValueFormatterForExport: false,
}

/** @deprecated */
export const chainColumnType: ColDef<unknown, string> = {
  cellRenderer: ChainCellRenderer,
  filter: "agTextColumnFilter", // NOTE! In practice this has no effect because collectionColumnType overrides this.
  filterParams: {
    cellRenderer: ChainCellRenderer,
    cellRendererParams: { isFilterRenderer: true },
  },
  getContextMenuItems(params, colDef) {
    const currentContextItems: Array<string | MenuItemDef> =
      params.defaultItems ?? []

    if (colDef.refData) {
      const refData = colDef.refData as Record<string, unknown>
      const projectData = refData[params.value] as
        | Record<string, any>
        | undefined

      if (projectData?.is_public && projectData?.data_id) {
        currentContextItems.push({
          name: "View chain",
          action: () => {
            window.open(`/explorer/projects/${projectData?.slug}`, "_blank")
          },
        })
      }
    }

    return currentContextItems
  },
  useValueFormatterForExport: false,
}

const getTimestampValue = (
  value: string | Date | null | { value?: string | Date }
) => {
  let timestamp: string | Date | null = null
  if (typeof value === "string") {
    timestamp = value
  } else if (value && "getTime" in value) {
    timestamp = value
  } else {
    timestamp = value?.value ?? null
  }

  if (timestamp) {
    const date = new Date(timestamp)
    if (date.toString() !== "Invalid Date") {
      return date
    }
  }
  return null
}

/** @deprecated */
export const dateTimeColumnType: ColDef<unknown, Date> = {
  tooltipValueGetter(params) {
    if (!params.value || isNaN(params.value as unknown as number)) {
      return null
    }

    return params.value.toISOString().split("T")[0]
  },
  filter: "agDateColumnFilter",
  filterParams: {
    filterOptions: [
      "lessThan",
      "lessThanOrEqual",
      "greaterThan",
      "greaterThanOrEqual",
      "inRange",
      "blank",
      "notBlank",
    ],
    blankText: "N/A",
  },
  chartDataType: "series",
  cellClass: "dateType",
  floatingFilterComponent: SimpleReadOnlyFloatingFilter,
  comparator: function dateComparator(
    date1?: Date | null,
    date2?: Date | null
  ) {
    if (isNaN(Number(date1)) || isNaN(Number(date2))) {
      return 0
    }

    if (!date1 || isNaN(Number(date1))) {
      return -1
    }
    if (!date2 || isNaN(Number(date2))) {
      return 1
    }

    return date1.getTime() - date2.getTime()
  },
  valueGetter({ data, column }) {
    // colId timestamp always returns a string
    // Other time related colIds return { value: "Timestamp string" }
    // timeAgo returns a Date object
    const value = (
      data as Record<string, null | string | Date | { value?: Date }>
    )?.[column.getColId()]

    return getTimestampValue(value)
  },
}

/** @deprecated */
export const dateColumnType: ColDef<unknown, Date> = {
  chartDataType: "time",
  cellClass: "dateType",
  filter: "agDateColumnFilter",
  filterParams: {
    filterOptions: [
      "equals",
      "lessThan",
      "lessThanOrEqual",
      "greaterThan",
      "greaterThanOrEqual",
      "inRange",
      "blank",
      "notBlank",
    ],
    blankText: "N/A",
  },
  floatingFilterComponent: SimpleReadOnlyFloatingFilter,
  valueGetter: ({ data, column }) => {
    // Date objects can be { value: "Timestamp string" } or "Timestamp string"
    const value = (data as Record<string, string | Date | { value?: Date }>)?.[
      column.getColId()
    ]

    return getTimestampValue(value)
  },
}

/** @deprecated */
export const marketSectorColumnType: ColDef<
  Record<string, string | null>,
  string
> = {
  valueGetter: createUnknownValueGetter("Unknown"),
  filterParams: {
    valueFormatter: (
      params: ValueFormatterParams<Record<string, string | null>, string>
    ) => params.value ?? "Unknown",
    comparator: compareStrings,
  },
  getContextMenuItems(params) {
    const currentContextItems: Array<MenuItemDef | string> =
      params.defaultItems ?? []
    const data = params?.node?.data

    if (data?.market_sector_id) {
      currentContextItems.push({
        name: "View market sector",
        action: () => {
          window.open(`/explorer/markets/${data.market_sector_id}`, "_blank")
        },
      })
    }

    return currentContextItems
  },
  useValueFormatterForExport: false,
}

const calcLightness = (percentageValue: number) => {
  const isExtremeValue = percentageValue > 75 || percentageValue < 5
  const baseLightness = isExtremeValue ? 40 : 50
  const middlepoint = 40 / 2

  return Math.round(
    baseLightness / (1 + Math.abs(middlepoint - percentageValue) / 100)
  )
}

const cohortPercentageCellStyle: CellStyleFunc<any, number> = ({ value }) => {
  if (typeof value !== "number") {
    return undefined
  }

  const percentageValue = value * 100
  const weighedPercentageValue = Math.min(100, percentageValue * 3)
  const hue = Math.round(weighedPercentageValue * (120 / 95))

  return {
    background: `hsla(${hue}, 85%, ${calcLightness(
      percentageValue
    )}%, var(--tt-ag-cohort-opacity))`,
    color: "hsl(var(--fg-default))",
  }
}

/** @deprecated */
export const cohortColumnType: ColDef<unknown, number> = {
  cellDataType: "percentage",
  chartDataType: "series",
  cellStyle: cohortPercentageCellStyle,
}

/** @deprecated */
export const currencyColorizedColumnType: ColDef<unknown, number> = {
  cellDataType: "currency",
  chartDataType: "series",
  cellStyle: (params) => {
    const value = params.value ?? 0
    if (value > 0) {
      return {
        color: "hsl(var(--fg-emerald))",
      }
    } else if (value < 0) {
      return {
        color: "hsl(var(--fg-red))",
      }
    }

    return {
      color: "hsl(var(--fg-default))",
    }
  },
}

/** @deprecated */
export const buyAndSellColumnType: ColDef<unknown, string> = {
  cellDataType: "collection",
  chartDataType: "category",
  cellStyle: (params) => {
    const value = params.value ? params.value.toLowerCase() : null
    if (value === "buy") {
      return {
        color: "hsl(var(--fg-emerald))",
      }
    } else if (value === "sell") {
      return {
        color: "hsl(var(--fg-red))",
      }
    }
  },
}
